import * as React from 'react'
import {
    DESKTOP_SCREEN_THRESHOLD as DESKTOP_SCREEN_THRESHOLD,
    FULLHD_SCREEN_THRESHOLD,
    HD_SCREEN_THRESHOLD,
    SMARTPHONE_TABLET_THRESHOLD,
    TABLET_RESPONSIVE_THRESHOLD,
} from './layout'

export namespace LayoutContext {
    export interface Value {
        screen: 'smartphone' | 'tablet' | 'desktop' | 'hd-desktop' | '2k-screen-desktop' | '4k-screen-desktop'
        layoutFamily: 'mobile' | 'responsive'
        width: number
        height: number
    }
}

const layoutMapper = (width: number, height: number): LayoutContext.Value => {
    if (width <= SMARTPHONE_TABLET_THRESHOLD) {
        return {
            screen: 'smartphone',
            layoutFamily: 'mobile',
            width: width,
            height: height,
        }
    }
    if (width <= TABLET_RESPONSIVE_THRESHOLD) {
        return {
            screen: 'tablet',
            layoutFamily: 'mobile',
            width: width,
            height: height,
        }
    }
    if (width <= DESKTOP_SCREEN_THRESHOLD) {
        return {
            screen: 'desktop',
            layoutFamily: 'mobile',
            width: width,
            height: height,
        }
    }
    if (width <= HD_SCREEN_THRESHOLD) {
        return {
            screen: 'hd-desktop',
            layoutFamily: 'responsive',
            width: width,
            height: height,
        }
    }
    if (width <= FULLHD_SCREEN_THRESHOLD) {
        return {
            screen: '2k-screen-desktop',
            layoutFamily: 'responsive',
            width: width,
            height: height,
        }
    }
    return {
        screen: '4k-screen-desktop',
        layoutFamily: 'responsive',
        width: width,
        height: height,
    }
}

export const LayoutContext = React.createContext<LayoutContext.Value>(
    layoutMapper(document.documentElement.clientWidth, document.documentElement.clientHeight)
)

export const LayoutCoductor: React.FC<{ children: React.ReactNode }> = props => {
    const [getLayoutDevice, setLayoutDevice] = React.useState<LayoutContext.Value>(
        layoutMapper(document.documentElement.clientWidth, document.documentElement.clientHeight)
    )

    const layoutCalculator = () =>
        setLayoutDevice(layoutMapper(document.documentElement.clientWidth, document.documentElement.clientHeight))

    React.useEffect(() => {
        window.addEventListener('resize', layoutCalculator)
        return () => window.removeEventListener('resize', layoutCalculator)
    }, [])

    return <LayoutContext.Provider value={getLayoutDevice}>{props.children}</LayoutContext.Provider>
}

export const useDeviceLayout = (): LayoutContext.Value => {
    const [deviceLayout, setDeviceLayout] = React.useState<LayoutContext.Value>(
        layoutMapper(document.documentElement.clientWidth, document.documentElement.clientHeight)
    )

    const layoutCalculator = () =>
        setDeviceLayout(layoutMapper(document.documentElement.clientWidth, document.documentElement.clientHeight))

    React.useEffect(() => {
        window.addEventListener('resize', layoutCalculator)
        return () => window.removeEventListener('resize', layoutCalculator)
    }, [])

    return deviceLayout
}
